import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { toggleDsPick } from 'redux/mode/actions';
import { DesignSystemToken } from 'designSystem/types';
import { formatTokenValue } from '../tokenUtil';
import * as Base from '../styles';
import * as S from './styles';

interface BorderRadiusScaleProps {
	tokens: DesignSystemToken[];
}

// Numeric estimate for sorting (px or rem→px)
const getRadiusSortValue = (token: DesignSystemToken): number => {
	if (token.value.kind !== 'borderRadius') return 0;
	if (token.value.length.px != null) return token.value.length.px;
	if (/rem$/i.test(token.value.length.original)) return token.value.length.numericValue * 16;
	return token.value.length.numericValue;
};

// Original CSS string for preview rendering
const getRadiusCss = (token: DesignSystemToken): string => {
	if (token.value.kind !== 'borderRadius') return '0px';
	return token.value.length.original;
};

const BorderRadiusScale = ({ tokens }: BorderRadiusScaleProps) => {
	const dispatch = useDispatch();
	const selectedPicks = useSelector((r: RootReducer) => r.mode.selectedDsPicks);

	const sorted = useMemo(
		() => [...tokens].sort((a, b) => getRadiusSortValue(a) - getRadiusSortValue(b)),
		[tokens]
	);

	const isSelected = (token: DesignSystemToken) =>
		selectedPicks.some((p) => p.kind === 'token' && p.cssVariable === token.cssVariable);

	return (
		<Base.ScaleRow>
			{sorted.map((token) => (
				<Base.ScaleItem
					key={token.id}
					type="button"
					$selected={isSelected(token)}
					onClick={() => dispatch(toggleDsPick({ kind: 'token', cssVariable: token.cssVariable }))}
				>
					<Base.ScaleTooltip>
						{token.cssVariable}
						<br />
						{formatTokenValue(token.value)}
					</Base.ScaleTooltip>
					<S.Box $radius={getRadiusCss(token)} />
					<Base.ScaleLabel>{token.name}</Base.ScaleLabel>
					<Base.ScaleValue>{formatTokenValue(token.value)}</Base.ScaleValue>
				</Base.ScaleItem>
			))}
		</Base.ScaleRow>
	);
};

export default BorderRadiusScale;
